import { useEffect } from "react";
import { getWSClient } from "@/lib/ws";
import { useWSStore } from "@/stores/ws";
import { usePresenceStore } from "@/stores/presence";
import { useAuthStore } from "@/stores/auth";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { ServerEvents } from "@/types";
import { playMessageSound } from "@/lib/sound";
import type {
  ConnectionEstablishedPayload,
  MessageCreatedPayload,
  MessageUpdatedPayload,
  ConversationStatusChangedPayload,
  ConversationAssignedPayload,
  ConversationUpdatedPayload,
  ConversationClassifiedPayload,
  TypingStartPayload,
  TypingStopPayload,
  PresenceUpdatePayload,
} from "@/types";

/**
 * Bridge between the WebSocket client and React state.
 * Connects on mount when companyId is truthy, disconnects on unmount.
 * Dispatches server push events to the appropriate stores and invalidates queries.
 */
export function useWebSocket(companyId: string) {
  const connected = useWSStore((s) => s.connected);
  const reconnecting = useWSStore((s) => s.reconnecting);
  const sessionId = useWSStore((s) => s.sessionId);

  useEffect(() => {
    if (!companyId) return;

    const ws = getWSClient();
    const wsStore = useWSStore.getState();
    const presenceStore = usePresenceStore.getState();

    // Wire up connection state callbacks
    ws.onConnectionChange = (isConnected: boolean) => {
      useWSStore.getState().setConnected(isConnected);
      if (!isConnected) {
        useWSStore.getState().setSessionId(null);
      }
    };

    ws.onReconnectingChange = (isReconnecting: boolean) => {
      useWSStore.getState().setReconnecting(isReconnecting);
    };

    // --- Server Push Event Handlers ---

    const handleConnectionEstablished = (params: Record<string, unknown>) => {
      const payload = params as unknown as ConnectionEstablishedPayload;
      useWSStore.getState().setSessionId(payload.sessionId);
    };

    const handleMessageCreated = (params: Record<string, unknown>) => {
      const payload = params as unknown as MessageCreatedPayload;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.messages.list(companyId, payload.conversationId),
      });
      // Also invalidate the conversation list since lastMessageAt / messageCount changed
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });

      // Clear typing indicator for the sender of this message.
      const message = params.message as Record<string, unknown> | undefined;
      const senderType = (message?.senderType ?? params.senderType) as string | undefined;
      if (senderType === "customer") {
        const customerId = (message?.senderId ?? params.senderId) as string | undefined;
        if (customerId) {
          usePresenceStore.getState().clearCustomerTyping(payload.conversationId, customerId);
        }
      } else if (senderType === "agent") {
        const agentId = (message?.senderId ?? params.senderId) as string | undefined;
        if (agentId) {
          usePresenceStore.getState().clearAgentTyping(payload.conversationId, agentId);
        }
      }

      // Play sound and show browser notification for incoming customer messages.
      if (senderType === "customer") {
        playMessageSound();

        // Browser notification when tab is hidden
        if (document.hidden && "Notification" in window) {
          const sender = message?.sender as { name?: string } | undefined;
          const senderName = (message?.senderName as string) || sender?.name || "Customer";
          const content = ((message?.content ?? params.content) as string) || "New message";
          const preview = content.length > 80 ? content.slice(0, 80) + "…" : content;

          if (Notification.permission === "granted") {
            const notif = new Notification(`${senderName} sent a message`, {
              body: preview,
              icon: "/favicon.ico",
              tag: `hp-msg-${payload.conversationId}`,
            });
            notif.onclick = () => {
              window.focus();
              notif.close();
            };
          } else if (Notification.permission === "default") {
            void Notification.requestPermission();
          }
        }
      }
    };

    const handleMessageUpdated = (params: Record<string, unknown>) => {
      const payload = params as unknown as MessageUpdatedPayload;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.messages.list(companyId, payload.conversationId),
      });
    };

    const handleConversationNew = (_params: Record<string, unknown>) => {
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });
    };

    const handleConversationStatusChanged = (params: Record<string, unknown>) => {
      const payload = params as unknown as ConversationStatusChangedPayload;
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(companyId, payload.conversationId),
      });
    };

    const handleConversationAssigned = (params: Record<string, unknown>) => {
      const payload = params as unknown as ConversationAssignedPayload;
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(companyId, payload.conversationId),
      });
    };

    const handleConversationUpdated = (params: Record<string, unknown>) => {
      const payload = params as unknown as ConversationUpdatedPayload;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(companyId, payload.conversationId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });
    };

    // Auto-clear timeouts for customer typing indicators, keyed by "conversationId:customerId"
    const customerTypingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

    const handleTypingStart = (params: Record<string, unknown>) => {
      const payload = params as unknown as TypingStartPayload;
      const currentUserId = useAuthStore.getState().user?.id;
      if (payload.userId) {
        // Ignore own typing events (from other WS connections)
        if (payload.userId === currentUserId) return;
        usePresenceStore.getState().setAgentTyping(
          payload.conversationId,
          payload.userId,
          payload.name,
        );
      } else if (payload.customerId) {
        usePresenceStore.getState().setCustomerTyping(
          payload.conversationId,
          payload.customerId,
          payload.name,
        );

        // Auto-clear after 3s if no new typing.start arrives
        const key = `${payload.conversationId}:${payload.customerId}`;
        const existing = customerTypingTimeouts.get(key);
        if (existing) clearTimeout(existing);
        customerTypingTimeouts.set(key, setTimeout(() => {
          usePresenceStore.getState().clearCustomerTyping(payload.conversationId, payload.customerId!);
          customerTypingTimeouts.delete(key);
        }, 3000));
      }
    };

    const handleTypingStop = (params: Record<string, unknown>) => {
      const payload = params as unknown as TypingStopPayload;
      const currentUserId = useAuthStore.getState().user?.id;
      if (payload.userId) {
        // Ignore own typing events (from other WS connections)
        if (payload.userId === currentUserId) return;
        usePresenceStore.getState().clearAgentTyping(
          payload.conversationId,
          payload.userId,
        );
      } else if (payload.customerId) {
        usePresenceStore.getState().clearCustomerTyping(
          payload.conversationId,
          payload.customerId,
        );

        // Clear the auto-clear timeout since we got an explicit stop
        const key = `${payload.conversationId}:${payload.customerId}`;
        const existing = customerTypingTimeouts.get(key);
        if (existing) {
          clearTimeout(existing);
          customerTypingTimeouts.delete(key);
        }
      }
    };

    const handleConversationClassified = (params: Record<string, unknown>) => {
      const payload = params as unknown as ConversationClassifiedPayload;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(companyId, payload.conversationId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });
    };

    const handleConversationEscalated = (_params: Record<string, unknown>) => {
      playMessageSound();
      void queryClient.invalidateQueries({
        queryKey: ["conversations", companyId],
        exact: false,
      });

      // Browser notification
      if ("Notification" in window && Notification.permission === "granted") {
        const notif = new Notification("Conversation escalated", {
          body: "A customer needs human assistance",
          icon: "/favicon.ico",
          tag: "hp-escalation",
        });
        notif.onclick = () => {
          window.focus();
          notif.close();
        };
      }
    };

    const handlePresenceUpdate = (params: Record<string, unknown>) => {
      const payload = params as unknown as PresenceUpdatePayload;
      if (payload.status === "offline") {
        usePresenceStore.getState().setAgentOffline(payload.userId);
      } else {
        usePresenceStore.getState().setAgentOnline(payload.userId, {
          firstName: payload.firstName,
          lastName: payload.lastName,
          avatarUrl: payload.avatarUrl,
          status: payload.status,
        });
      }
    };

    // Register all event handlers
    ws.on(ServerEvents.CONNECTION_ESTABLISHED, handleConnectionEstablished);
    ws.on(ServerEvents.MESSAGE_CREATED, handleMessageCreated);
    ws.on(ServerEvents.MESSAGE_UPDATED, handleMessageUpdated);
    ws.on(ServerEvents.CONVERSATION_NEW, handleConversationNew);
    ws.on(ServerEvents.CONVERSATION_STATUS_CHANGED, handleConversationStatusChanged);
    ws.on(ServerEvents.CONVERSATION_ASSIGNED, handleConversationAssigned);
    ws.on(ServerEvents.CONVERSATION_UPDATED, handleConversationUpdated);
    ws.on(ServerEvents.CONVERSATION_CLASSIFIED, handleConversationClassified);
    ws.on(ServerEvents.TYPING_START, handleTypingStart);
    ws.on(ServerEvents.TYPING_STOP, handleTypingStop);
    ws.on(ServerEvents.PRESENCE_UPDATE, handlePresenceUpdate);
    ws.on(ServerEvents.CONVERSATION_ESCALATED, handleConversationEscalated);

    // Connect
    ws.connect(companyId);

    return () => {
      // Unregister all event handlers
      ws.off(ServerEvents.CONNECTION_ESTABLISHED, handleConnectionEstablished);
      ws.off(ServerEvents.MESSAGE_CREATED, handleMessageCreated);
      ws.off(ServerEvents.MESSAGE_UPDATED, handleMessageUpdated);
      ws.off(ServerEvents.CONVERSATION_NEW, handleConversationNew);
      ws.off(ServerEvents.CONVERSATION_STATUS_CHANGED, handleConversationStatusChanged);
      ws.off(ServerEvents.CONVERSATION_ASSIGNED, handleConversationAssigned);
      ws.off(ServerEvents.CONVERSATION_UPDATED, handleConversationUpdated);
      ws.off(ServerEvents.CONVERSATION_CLASSIFIED, handleConversationClassified);
      ws.off(ServerEvents.TYPING_START, handleTypingStart);
      ws.off(ServerEvents.TYPING_STOP, handleTypingStop);
      ws.off(ServerEvents.PRESENCE_UPDATE, handlePresenceUpdate);
      ws.off(ServerEvents.CONVERSATION_ESCALATED, handleConversationEscalated);

      ws.onConnectionChange = null;
      ws.onReconnectingChange = null;

      // Clear all customer typing auto-clear timeouts
      for (const timeout of customerTypingTimeouts.values()) {
        clearTimeout(timeout);
      }
      customerTypingTimeouts.clear();

      ws.disconnect();
      wsStore.reset();
      presenceStore.reset();
    };
  }, [companyId]);

  return { connected, reconnecting, sessionId };
}
